import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Gift, Mail, RefreshCw, Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { usePublicOrgContext } from "@/contexts/PublicOrgContext";
// UPPDRAG-19 PR-A: open-redirect-safe returnUrl reader.
import { safeReturnUrl } from "@/lib/authRequiredHelpers";

export default function Auth() {
  const { t } = useTranslation('auth');
  // UPPDRAG-19 PR-A: read ?mode=signup|login from the query string
  // (set by AuthRequiredModal). Defaults to login (the historic
  // behaviour) when missing or any other value.
  const [searchParams] = useSearchParams();
  const initialIsLogin = searchParams.get("mode") !== "signup";
  const [isLogin, setIsLogin] = useState(initialIsLogin);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const birthDate = useMemo(() => {
    if (!birthDay || !birthMonth || !birthYear) return "";
    return `${birthYear}-${birthMonth.padStart(2, "0")}-${birthDay.padStart(2, "0")}`;
  }, [birthDay, birthMonth, birthYear]);
  const [publicProfile, setPublicProfile] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [showReferral, setShowReferral] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  // (searchParams declared above for UPPDRAG-19 PR-A mode-handling.)

  // Auto-fill referral code from URL
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setShowReferral(true);
      setIsLogin(false); // Switch to signup mode
    }
  }, [searchParams]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    try {
      const { error } = await supabase.auth.resend({ type: "signup", email: signupEmail });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success(t('confirmationEmailResent'));
        setResendCooldown(60);
      }
    } catch {
      toast.error(t('couldNotSendEmail'));
    }
  };

  // Org för branding + signup-scope kommer ENBART från hostname-resolution
  // (PublicOrgContext). När hostname inte matchar någon `organization_domains`-rad
  // visas generic "Klubben"-placeholder (logoLetter "K") och signup blockeras
  // i handleSubmit (publicOrg-check). Detta är multi-tenant-korrekt: ingen
  // silent fallback till "första org" vid felmappad hostname.
  //
  // PR-AF removed the legacy single-tenant fallback query (`SELECT … LIMIT 1`)
  // som plockade godtycklig org. Den behövs inte: prod-domäner ligger i
  // organization_domains, dev-läge använder VITE_DEV_DEFAULT_ORG_SLUG via
  // usePublicOrg.
  //
  // Ref: audit-reports/tech-debt-cleanup/01-investigation.md (Item 1)
  const { org: publicOrg } = usePublicOrgContext();
  const org = publicOrg ?? null;
  const orgName = org?.name || "Klubben";
  const orgSettings = (org?.settings as Record<string, any>) ?? {};
  const logoLetter = orgName.charAt(0).toUpperCase();

  // Check if public signup is enabled for this org
  const { data: signupEnabled } = useQuery({
    queryKey: ["public-signup-enabled", org?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("feature_flags")
        .select("public_signup_enabled")
        .eq("organization_id", org!.id)
        .maybeSingle();
      return data?.public_signup_enabled ?? false;
    },
    enabled: !!org?.id,
  });

  // Force login mode when signup is disabled (treat undefined/loading as disabled)
  useEffect(() => {
    if (signupEnabled !== true && !isLogin) {
      setIsLogin(true);
    }
  }, [signupEnabled, isLogin]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth`,
      });
      if (error) {
        toast.error(error.message);
      } else {
        setResetSent(true);
      }
    } catch {
      toast.error(t('errors:generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Belt-and-suspenders: block signup even if UI somehow allowed it
    if (!isLogin && signupEnabled !== true) {
      toast.error(t('signupDisabledMessage'));
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          toast.error(error.message);
        } else {
          // UPPDRAG-19 PR-A: respect ?returnUrl=… so users dropped here
          // by AuthRequiredModal land back where they came from. The
          // safeReturnUrl helper guards against open-redirect (absolute
          // URLs / protocol-relative paths get coerced back to "/").
          navigate(safeReturnUrl(searchParams.get("returnUrl")));
        }
      } else {
        // Check if under 18
        const isMinor = birthDate
          ? (new Date().getFullYear() - new Date(birthDate).getFullYear()) < 18
          : false;

        if (isMinor) {
          toast.error(t('juniorNeedsParent'));
          setLoading(false);
          return;
        }

        // F-4: Atomic signup via edge function. Edge function kör:
        //   auth.admin.createUser → customers.insert → profiles.update
        // i sekvens, med rollback om customers-INSERT failar (deleteUser).
        // Ref: audit-reports/feat-domain-mapping-audit/rapport.md F-4
        if (!publicOrg?.id) {
          toast.error("Klubb saknas — kontakta support");
          setLoading(false);
          return;
        }

        const { data: signupResponse, error: signupErr } = await supabase.functions.invoke(
          "signup-with-customer",
          {
            body: {
              email,
              password,
              fullName: fullName || null,
              organizationId: publicOrg.id,
              birthDate: birthDate || null,
              publicProfile,
            },
          },
        );

        // Edge function returnerar { success, userId } eller { error }
        const signupData = signupResponse as { success?: boolean; userId?: string; error?: string } | null;
        const error = signupErr || (signupData && !signupData.success
          ? new Error(signupData.error || "Signup misslyckades")
          : null);

        // Efter atomic signup, logga in så session etableras. Faller tyst
        // om email-bekräftelse är påtvingad (då får user logga in efter verify).
        let signUpData: { user: { id: string } | null } = { user: null };
        if (!error && signupData?.userId) {
          await supabase.auth.signInWithPassword({ email, password }).catch((): null => null);
          signUpData = { user: { id: signupData.userId } };
        }

        if (error) {
          toast.error(error.message);
        } else {
          // Profile + customer-rad hanterade av edge function. Ingen ytterligare
          // write här. Men behåll if-blocket nedan för referral-kod-processing.

          // Process referral code if provided
          if (referralCode && signUpData?.user) {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              const res = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-referral`,
                {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${session?.access_token}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ code: referralCode }),
                }
              );
              const json = await res.json();
              if (json.success) {
                toast.success(t('referralBonus', { bonus: json.welcomeBonus, name: json.referrerName }), {
                  duration: 6000,
                });
              }
            } catch (err) {
              console.warn("Referral processing failed:", err);
            }
          }

          setSignupEmail(email);
          setSignupComplete(true);
        }
      }
    } catch (error: any) {
      if (error?.message?.includes("Failed to fetch")) {
        toast.error(t('backendUnreachable'));
      } else {
        toast.error(t('authError'));
      }
      console.error("Auth submit error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      {signupComplete ? (
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-6 text-center space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold">{t('confirmYourEmail')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('confirmationEmailSentTo')}{" "}
                <span className="font-medium text-foreground">{signupEmail}</span>.
                {t('clickLinkToActivate')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('checkSpamFolder')}
              </p>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleResendEmail}
                disabled={resendCooldown > 0}
                className="w-full gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {resendCooldown > 0 ? t('resendEmailCooldown', { seconds: resendCooldown }) : t('resendEmail')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setSignupComplete(false); setIsLogin(true); }}
                className="w-full"
              >
                {t('backToLogin')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {org?.logo_url ? (
            <img src={org.logo_url} alt={orgName} className="mx-auto mb-4 h-14 max-w-[200px] object-contain" />
          ) : (
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
              <span className="text-2xl font-bold text-primary-foreground">{logoLetter}</span>
            </div>
          )}
          <CardTitle className="text-2xl">{isLogin ? t('login') : t('createAccount')}</CardTitle>
          <CardDescription>
            {isLogin
              ? (orgSettings.login_text || t('welcomeBack', { org: orgName }))
              : (orgSettings.welcome_text || t('getStarted', { org: orgName }))}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {showResetPassword ? (
            resetSent ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('resetLinkSent')}
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => { setShowResetPassword(false); setResetSent(false); setResetEmail(""); }}
                >
                  {t('backToLogin')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resetEmail">{t('email')}</Label>
                  <Input
                    id="resetEmail"
                    type="email"
                    placeholder="din@email.se"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="animate-spin" />}
                  {t('sendResetLink')}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setShowResetPassword(false); setResetEmail(""); }}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {t('backToLogin')}
                  </button>
                </div>
              </form>
            )
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="fullName">{t('fullName')}</Label>
                <Input
                  id="fullName"
                  placeholder={t('yourName')}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required={!isLogin}
                />
              </div>
            )}
            {!isLogin && (
              <div className="space-y-2">
                <Label>{t('birthDateOptional')}</Label>
                <div className="grid grid-cols-3 gap-2">
                  <select
                    value={birthDay}
                    onChange={(e) => setBirthDay(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option value="">{t('day')}</option>
                    {Array.from({ length: (() => {
                      if (!birthMonth) return 31;
                      const m = parseInt(birthMonth);
                      if ([4,6,9,11].includes(m)) return 30;
                      if (m === 2) {
                        const y = parseInt(birthYear) || 2000;
                        return (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) ? 29 : 28;
                      }
                      return 31;
                    })() }, (_, i) => (
                      <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                    ))}
                  </select>
                  <select
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option value="">{t('month')}</option>
                    {["Januari","Februari","Mars","April","Maj","Juni","Juli","Augusti","September","Oktober","November","December"].map((name, i) => (
                      <option key={i + 1} value={String(i + 1)}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option value="">{t('year')}</option>
                    {Array.from({ length: new Date().getFullYear() - 1919 }, (_, i) => {
                      const y = new Date().getFullYear() - i;
                      return <option key={y} value={String(y)}>{y}</option>;
                    })}
                  </select>
                </div>
                {birthDate && (new Date().getFullYear() - new Date(birthDate).getFullYear()) < 18 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    {t('under18Warning')}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="din@email.se"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            {!isLogin && (
              <div className="space-y-2">
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setShowReferral(!showReferral)}
                >
                  <Gift className="h-3.5 w-3.5" />
                  {showReferral ? t('hideReferralCode') : t('referralCode')}
                </button>
                {showReferral && (
                  <Input
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="T.ex. RICKARD26"
                  />
                )}
              </div>
            )}
            {!isLogin && (
              <div className="rounded-lg border p-4 space-y-3">
                <Label className="text-sm font-medium">{t('privacy')}</Label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={publicProfile}
                    onChange={(e) => setPublicProfile(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  />
                  <div>
                    <p className="text-sm font-medium">{t('showProfileInPlayerList')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('showProfileInPlayerListDesc')}
                    </p>
                  </div>
                </label>
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="animate-spin" />}
              {isLogin ? t('login') : t('createAccount')}
            </Button>
            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setShowResetPassword(true); setResetEmail(email); }}
                  className="text-sm text-muted-foreground hover:text-primary hover:underline"
                >
                  {t('forgotPassword')}
                </button>
              </div>
            )}
          </form>
          )}
          {signupEnabled !== true ? (
            <Alert className="mt-4" variant="default">
              <Info className="h-4 w-4" />
              <AlertTitle>{t('signupDisabledTitle')}</AlertTitle>
              <AlertDescription>{t('signupDisabledMessage')}</AlertDescription>
            </Alert>
          ) : (
            <div className="mt-4 text-center text-sm text-muted-foreground">
              {isLogin ? t('noAccount') : t('hasAccount')}{" "}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="font-medium text-primary hover:underline"
              >
                {isLogin ? t('createAccount') : t('login')}
              </button>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
